import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { pendingApprovalCountKey } from "@/hooks/usePendingApprovalCount";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Loader2,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Send,
  Users,
  Info,
  Lightbulb,
  MoreHorizontal,
  Terminal,
  ImageOff,
  Image as ImageIcon,
  Sparkles,
  UserRound,
  Workflow,
} from "lucide-react";
import { formatDistanceToNow, format } from "date-fns";
import { toast } from "sonner";

// Types matching the database schema
interface AuditLog {
  id: string;
  action_name: string;
  action_version: string;
  executor_type: string;
  status: 'pending_approval' | 'running' | 'success' | 'failed' | 'rejected';
  latency_ms: number | null;
  inputs: any;
  outputs: any;
  error: string | null;
  created_at: string;
}

interface ApprovalRequest {
  id: string;
  audit_log_id: string;
  action_name: string;
  inputs: any;
  reasoning_trace: string | null;
  status: 'pending' | 'approved' | 'rejected' | 'expired';
  created_at: string;
}

// Single source of truth for action-type metadata. `label` is the
// human-readable (sentence-case) name shown in the tables and help dialog.
const ACTION_TYPES: Record<
  string,
  { label: string; tier: number; approval: string; desc: string }
> = {
  send_message: { label: "Send message", tier: 1, approval: "Autonomous", desc: "Sends an outbound message (WhatsApp / Instagram / email / web)." },
  create_reminder: { label: "Create reminder", tier: 2, approval: "Autonomous", desc: "Schedules a future message." },
  escalate_conversation: { label: "Escalate conversation", tier: 2, approval: "Autonomous", desc: "Hands off to a human and sends a handoff email." },
  open_case: { label: "Open case", tier: 2, approval: "Autonomous", desc: "Creates a trackable human-owned customer case or incident." },
  pause_outreach: { label: "Pause customer outreach", tier: 2, approval: "Autonomous", desc: "Stops proactive workflow and campaign messages for a customer." },
  create_booking: { label: "Create booking", tier: 3, approval: "Autonomous (logged)", desc: "Creates a pay-at-venue booking." },
  cancel_booking: { label: "Cancel booking", tier: 3, approval: "Autonomous (logged)", desc: "Cancels a booking." },
  reschedule_booking: { label: "Reschedule booking", tier: 3, approval: "Autonomous (logged)", desc: "Changes a booking's date / time." },
  send_payment_link: { label: "Send payment link", tier: 4, approval: "Requires approval", desc: "Creates a Stripe Checkout session and sends the payment link." },
  winback_batch: { label: "Execute winback campaign", tier: 0, approval: "Always reviewed", desc: "A batch of dormant-customer outreach held for recipient-level review." },
  external_action: { label: "Run action", tier: 4, approval: "Requires approval", desc: "Performs an owner-defined action in an external system (place an order, submit a request, update a record)." },
  mcp_action: { label: "Run MCP tool", tier: 4, approval: "Requires approval", desc: "Runs an action tool on the business's connected MCP server." },
  reply_to_complaint: { label: "Send complaint response", tier: 4, approval: "Always requires approval", desc: "Publishes the drafted Google reply or emails the complainant." },
  issue_recovery_offer: { label: "Issue recovery offer", tier: 4, approval: "Always requires approval", desc: "Creates and sends a one-use credit or discount within the saved guardrails." },
};

const formatActionName = (name: string) =>
  ACTION_TYPES[name]?.label ??
  name.charAt(0).toUpperCase() + name.slice(1).replace(/_/g, " ");

const RiskTier = ({ name }: { name: string }) => {
  const tier = ACTION_TYPES[name]?.tier;
  if (!tier) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <Badge
      variant="outline"
      className={
        tier >= 4
          ? "bg-amber-500/10 text-amber-600 border-amber-200 hover:bg-amber-500/10 text-xs font-medium whitespace-nowrap"
          : "bg-muted text-muted-foreground border-border hover:bg-muted text-xs font-medium whitespace-nowrap"
      }
    >
      Tier {tier}
    </Badge>
  );
};

// Explanation panel shown from the "?" beside the Action column header.
// Mirrors the MAC explanation Dialog shell in PricingSection.tsx.
const ActionTypesInfoContent = () => (
  <div className="p-6 sm:p-8 md:p-10 max-h-[85vh] overflow-y-auto">
    <div className="mb-6">
      <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full border border-primary/20 mb-4">
        <Lightbulb className="w-3 h-3" />
        How actions work
      </div>
      <h3 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight text-foreground mb-3">
        Every action carries a{" "}
        <span className="text-primary italic">risk tier.</span>
      </h3>
      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed font-medium">
        The AI agent can take these actions on your behalf. Lower-tier actions
        run autonomously; higher-tier ones are held here for your approval
        before they execute.
      </p>
    </div>

    <div className="rounded-xl border border-input/40 overflow-hidden bg-white/40 mb-6">
      <Table>
        <TableHeader>
          <TableRow className="hover:bg-transparent [&>th]:bg-background">
            <TableHead>Action</TableHead>
            <TableHead className="whitespace-nowrap">Risk tier</TableHead>
            <TableHead>Approval</TableHead>
            <TableHead>What it does</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {Object.entries(ACTION_TYPES).map(([key, a]) => (
            <TableRow
              key={key}
              className={
                key === "send_payment_link"
                  ? "bg-primary/5 hover:bg-primary/10"
                  : "hover:bg-muted/30"
              }
            >
              <TableCell>
                <div className="font-semibold text-foreground text-sm">{a.label}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5 font-mono">{key}</div>
              </TableCell>
              <TableCell className="text-sm text-foreground">
                {a.tier === 0 ? "—" : a.tier}
              </TableCell>
              <TableCell>
                <Badge
                  variant="outline"
                  className={
                    a.approval === "Requires approval" || a.approval === "Always reviewed"
                      ? "bg-amber-500/10 text-amber-600 border-amber-200 hover:bg-amber-500/10 text-xs font-medium whitespace-nowrap"
                      : "bg-emerald-500/10 text-emerald-600 border-emerald-200 hover:bg-emerald-500/10 text-xs font-medium whitespace-nowrap"
                  }
                >
                  {a.approval}
                </Badge>
              </TableCell>
              <TableCell className="text-xs text-muted-foreground leading-relaxed">
                {a.desc}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>

    <div className="rounded-xl bg-background/60 border border-input/40 p-5 sm:p-6">
      <p className="text-sm font-bold uppercase tracking-widest text-primary mb-2 flex items-center gap-2">
        <Info className="w-3.5 h-3.5" />
        Good to know
      </p>
      <p className="text-sm text-foreground/80 leading-relaxed font-medium">
        By default only{" "}
        <span className="font-bold text-foreground">tier 4+</span> actions
        require approval, so{" "}
        <span className="font-bold text-foreground">Send payment link</span>{" "}
        and campaign batches are what you'll usually see here. Any action can be
        configured to require approval via per-action policy.
      </p>
    </div>
  </div>
);

const ActionHeaderCell = () => (
  <TableHead>
    <span className="inline-flex items-center gap-1.5">
      Action
      <Dialog>
        <DialogTrigger asChild>
          <button
            type="button"
            aria-label="What are the action types?"
            className="text-muted-foreground/60 hover:text-primary transition-colors"
          >
            <Info className="w-3.5 h-3.5" />
          </button>
        </DialogTrigger>
        <DialogContent className="w-[calc(100%-2rem)] max-w-5xl p-0 overflow-hidden rounded-2xl border-input/40">
          <ActionTypesInfoContent />
        </DialogContent>
      </Dialog>
    </span>
  </TableHead>
);

// Who carried out a logged action, straight from the log's executor_type.
const EXECUTORS: Record<string, { label: string; icon: typeof Sparkles }> = {
  ai: { label: "AI", icon: Sparkles },
  workflow: { label: "Workflow", icon: Workflow },
  human: { label: "Team member", icon: UserRound },
};
const executorFor = (executorType: string) =>
  EXECUTORS[executorType] ?? { label: executorType, icon: Sparkles };

// Dev-only sample rows so the approval-queue UI can be vetted without seeding
// the database. Never shown in a production build (import.meta.env.DEV), and
// only when the real queue is empty. Their ids are prefixed "sample-" so the
// approve/reject handlers can short-circuit instead of calling edge functions.
const SAMPLE_APPROVALS: ApprovalRequest[] = [
  {
    id: "sample-1",
    audit_log_id: "sample-audit-1",
    action_name: "send_payment_link",
    inputs: {
      amount: 4500,
      currency: "sgd",
      description: "Signature Facial — 60 min",
      customer_name: "Jess Tan",
      customer_phone: "+65 8123 4567",
      customer_email: "jess.tan@gmail.com",
    },
    reasoning_trace:
      "Jess confirmed she'd like to book the Signature Facial for Saturday and pay online.",
    status: "pending",
    created_at: new Date(Date.now() - 1000 * 60 * 6).toISOString(),
  },
  {
    id: "sample-2",
    audit_log_id: "sample-audit-2",
    action_name: "send_message",
    inputs: {
      channel: "whatsapp",
      customer_name: "Jess Tan",
      customer_phone: "+65 8123 4567",
      customer_email: "jess.tan@gmail.com",
      content: {
        text: "Hi Jess! You're all set for Saturday at 2pm with our Signature Facial. We've got you down for 60 minutes — see you then! Let me know if anything changes. 🌿",
      },
    },
    reasoning_trace: null,
    status: "pending",
    created_at: new Date(Date.now() - 1000 * 60 * 38).toISOString(),
  },
  {
    id: "sample-3",
    audit_log_id: "sample-audit-3",
    action_name: "winback_batch",
    inputs: {
      campaign_id: "dormant_winback:spring_glow",
      recipients: [
        {
          contact_id: "00000000-0000-0000-0000-000000000001",
          channel: "whatsapp",
          decision_trace: {
            signals: {
              synopsis: "Regular before going quiet — last visited for a facial.",
              days_since_last_activity: 74,
              last_booking_value: 90,
              total_bookings: 6,
              ltv: 540,
            },
          },
        },
        {
          contact_id: "00000000-0000-0000-0000-000000000002",
          channel: "whatsapp",
          decision_trace: {
            signals: {
              synopsis: "First-timer who hasn't rebooked.",
              days_since_last_activity: 63,
              last_booking_value: 45,
              total_bookings: 1,
              ltv: 45,
            },
          },
        },
      ],
    },
    reasoning_trace:
      "2 customers haven't booked in 60+ days. Drafted a gentle spring win-back with a returning-guest offer.",
    status: "pending",
    created_at: new Date(Date.now() - 1000 * 60 * 95).toISOString(),
  },
];

// Pulls a customer identity out of an action's free-form `inputs` so the queue
// can be organised around *who* an action affects. Approval-gated actions
// (payment links, bookings) carry the customer fields directly; chat- or
// booking-only actions fall back to a conversation / booking reference. Purely
// client-side. No extra lookups.
const resolveContact = (
  inputs: any,
): { key: string; name: string; detail: string | null } | null => {
  const i = inputs ?? {};
  const name = i.customer_name ?? i.contact?.name ?? null;
  const phone = i.customer_phone ?? i.contact?.phone ?? null;
  const email = i.customer_email ?? i.contact?.email ?? null;
  if (name || phone || email) {
    // Prefer email, then phone, then name as the stable grouping key so the
    // same person's actions land in one group regardless of which action it is.
    const id = String(email || phone || name).toLowerCase();
    return { key: `c:${id}`, name: name ?? phone ?? email, detail: name ? phone ?? email ?? null : null };
  }
  if (i.conversation_id) return { key: `conv:${i.conversation_id}`, name: "Chat conversation", detail: "Linked conversation" };
  if (i.booking_id) return { key: `bk:${i.booking_id}`, name: `Booking ${String(i.booking_id).slice(0, 8)}`, detail: "Booking action" };
  return null;
};

type ContactGroup = { key: string; name: string; detail: string | null; items: ApprovalRequest[] };

// Splits the queue into per-contact groups (so one customer's actions sit
// together) and cross-contact batches (campaigns, which have no single owner).
// Contact order follows first appearance, which preserves the newest-first sort.
const groupQueue = (
  queue: ApprovalRequest[],
): { contactGroups: ContactGroup[]; batches: ApprovalRequest[] } => {
  const batches: ApprovalRequest[] = [];
  const order: string[] = [];
  const map = new Map<string, ContactGroup>();
  for (const req of queue) {
    if (req.action_name === "winback_batch") {
      batches.push(req);
      continue;
    }
    const c = resolveContact(req.inputs);
    const key = c?.key ?? `action:${req.id}`;
    const existing = map.get(key);
    if (existing) {
      existing.items.push(req);
    } else {
      map.set(key, { key, name: c?.name ?? "Unknown contact", detail: c?.detail ?? null, items: [req] });
      order.push(key);
    }
  }
  return { contactGroups: order.map((k) => map.get(k)!), batches };
};

const ActionCenter = ({ view = "queue" }: { view?: "queue" | "audit" } = {}) => {
  const { businessId } = useAuth();
  const [batchModalId, setBatchModalId] = useState<string | null>(null);
  const [actionModalId, setActionModalId] = useState<string | null>(null);
  const [auditModalId, setAuditModalId] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [chosenQueueTab, setChosenQueueTab] = useState<string | null>(null);
  // Per-batch hero-image override for the review modal (one batch open at a time).
  const [batchIncludeImages, setBatchIncludeImages] = useState(true);

  // Queries
  const { data: approvalQueue, isLoading: loadingQueue, refetch: refetchQueue } = useQuery({
    queryKey: ["action_approval_queue", businessId],
    queryFn: async (): Promise<ApprovalRequest[]> => {
      if (!businessId) return [];
      const { data, error } = await supabase
        .from("action_approval_queue")
        .select("*")
        .eq("business_id", businessId)
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!businessId,
  });

  const { data: auditLogs, isLoading: loadingAudit, refetch: refetchAudit } = useQuery({
    queryKey: ["action_audit_logs", businessId],
    queryFn: async (): Promise<AuditLog[]> => {
      if (!businessId) return [];
      const { data, error } = await supabase
        .from("action_audit_logs")
        .select("*")
        .eq("business_id", businessId)
        .order("created_at", { ascending: false })
        .limit(50);
      if (error) throw error;
      return data ?? [];
    },
    enabled: !!businessId,
  });

  // Business-wide image default. Seeds the per-batch toggle in the review
  // modal. Shares its cache key with the Campaigns settings toggle.
  const { data: businessImagePref = true } = useQuery({
    queryKey: ["winback_include_images", businessId],
    enabled: !!businessId,
    queryFn: async (): Promise<boolean> => {
      const { data } = await (supabase as any)
        .from("ai_agent_configs")
        .select("winback_include_images")
        .eq("business_id", businessId)
        .maybeSingle();
      return data?.winback_include_images !== false;
    },
  });

  // When the batch modal opens, seed the per-batch toggle from the business
  // default so the owner sees the current setting and can override for this batch.
  useEffect(() => {
    if (batchModalId) setBatchIncludeImages(businessImagePref);
  }, [batchModalId, businessImagePref]);

  const queryClient = useQueryClient();
  // After a decision, refresh the list here and the sidebar's Approvals count.
  const refreshAfterDecision = () => {
    refetchQueue();
    refetchAudit();
    queryClient.invalidateQueries({ queryKey: pendingApprovalCountKey(businessId) });
  };

  const handleRefresh = () => {
    refetchQueue();
    refetchAudit();
    toast.success("Data refreshed");
  };

  // Approving must actually run the action, not just flip the queue row.
  // execute-approved-action resumes it through the ActionEngine (sends the
  // held reply, creates the payment link, etc.). A plain DB update would leave
  // the action approved but never executed.
  const handleDecision = async (id: string, decision: 'approve' | 'reject') => {
    if (id.startsWith("sample-")) {
      toast.message("Sample action — not executed (dev preview only)");
      return;
    }
    setProcessingId(id);
    try {
      const { data, error } = await (supabase.functions as any).invoke("execute-approved-action", {
        body: { queue_id: id, decision },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(decision === 'approve' ? "Approved and sent" : "Rejected");
      refreshAfterDecision();
    } catch (e: any) {
      toast.error(e.message || "Failed to process decision");
    } finally {
      setProcessingId(null);
    }
  };

  // Winback batches route through a dedicated edge function (it flips status
  // and then sends each recipient). Plain DB update would leave the batch
  // approved but never sent.
  const [processingBatchId, setProcessingBatchId] = useState<string | null>(null);
  const handleBatchDecision = async (id: string, decision: 'approve' | 'reject') => {
    if (id.startsWith("sample-")) {
      toast.message("Sample batch — not executed (dev preview only)");
      return;
    }
    setProcessingBatchId(id);
    try {
      const { data, error } = await (supabase.functions as any).invoke("execute-winback-batch", {
        body: { batch_id: id, decision, include_images: batchIncludeImages },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      if (decision === "approve") {
        toast.success(`Batch approved — sent to ${data.contacted ?? 0} recipient${data.contacted === 1 ? "" : "s"}${data.errors ? `, ${data.errors} failed` : ""}`);
      } else {
        toast.success("Batch rejected");
      }
      refreshAfterDecision();
    } catch (e: any) {
      toast.error(e.message || "Failed to process batch");
    } finally {
      setProcessingBatchId(null);
    }
  };

  // A queued AI reply stores its proposed text at inputs.content.text (a
  // WhatsApp template at inputs.content.fallbackText). When present, we show
  // the message itself rather than a raw inputs blob so the owner can read
  // exactly what will be sent before approving.
  const replyDraftText = (inputs: any): string | null => {
    const t = inputs?.response ?? inputs?.content?.text ??
      inputs?.content?.fallbackText;
    return typeof t === "string" ? t : null;
  };

  const statusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case 'success':
        return <Badge variant="outline" className="bg-emerald-500/10 text-emerald-600 border-emerald-200 hover:bg-emerald-500/10 text-xs font-medium">Success</Badge>;
      case 'failed':
      case 'rejected':
        return <Badge variant="outline" className="bg-red-500/10 text-red-600 border-red-200 hover:bg-red-500/10 text-xs font-medium">{status.charAt(0).toUpperCase() + status.slice(1)}</Badge>;
      case 'pending':
      case 'pending_approval':
      case 'running':
        return <Badge variant="outline" className="bg-amber-500/10 text-amber-600 border-amber-200 hover:bg-amber-500/10 text-xs font-medium">Pending</Badge>;
      default:
        return <Badge variant="outline" className="text-xs font-medium">{status}</Badge>;
    }
  };

  // Dev-only: fall back to sample rows when the real queue is empty, so the UI
  // can be vetted. Production builds always use the live queue.
  const queue: ApprovalRequest[] =
    import.meta.env.DEV && (approvalQueue ?? []).length === 0
      ? SAMPLE_APPROVALS
      : approvalQueue ?? [];

  // Organise the queue around the contact each action affects, with campaigns
  // (which span many contacts) kept in their own section.
  const { contactGroups, batches } = groupQueue(queue);
  // Open on the tab that has something waiting: an owner whose only pending
  // items are campaign batches would otherwise land on an empty "By contact".
  const queueTab = chosenQueueTab
    ?? (contactGroups.length === 0 && batches.length > 0 ? "campaigns" : "individuals");

  // Rows open a review modal on click.
  const selectedBatch = queue.find((r) => r.id === batchModalId) ?? null;
  const selectedAction = queue.find((r) => r.id === actionModalId) ?? null;
  const selectedAudit = (auditLogs ?? []).find((l) => l.id === auditModalId) ?? null;

  return (
    <div className="w-full space-y-6 animate-in fade-in duration-500">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1 min-w-0">
          <h2 className="text-xl font-heading font-semibold text-foreground">
            {view === "audit" ? "Audit logs" : "Approvals"}
          </h2>
          <p className="text-sm text-muted-foreground">
            {view === "audit"
              ? "A record of every action your agents have taken."
              : "Review and approve pending actions before they run."}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} className="h-9 self-start shrink-0">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* The Approval queue / Audit logs split now lives in the sidebar (under
          Actions); this page renders one of those two views. The approval queue
          itself is split into "By contact" and "Campaigns" sub-tabs. */}
      {view === "queue" ? (
        <Tabs value={queueTab} onValueChange={setChosenQueueTab} className="w-full">
            <TabsList className="bg-transparent border-b border-border p-0 w-full justify-start rounded-none h-auto gap-6 mb-6">
              <TabsTrigger
                value="individuals"
                className="bg-transparent rounded-none border-b-2 border-transparent px-0 pb-3 pt-2 text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none font-semibold text-sm transition-all flex items-center gap-2"
              >
                <Users className="h-4 w-4" />
                By contact
                {contactGroups.length ? (
                  <span className="ml-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
                    {contactGroups.length}
                  </span>
                ) : null}
              </TabsTrigger>
              <TabsTrigger
                value="campaigns"
                className="bg-transparent rounded-none border-b-2 border-transparent px-0 pb-3 pt-2 text-muted-foreground data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none font-semibold text-sm transition-all flex items-center gap-2"
              >
                <Send className="h-4 w-4" />
                Campaigns
                {batches.length ? (
                  <span className="ml-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold text-primary-foreground">
                    {batches.length}
                  </span>
                ) : null}
              </TabsTrigger>
            </TabsList>

            {/* By contact — single-contact actions grouped by the customer they affect. */}
            <TabsContent value="individuals" className="mt-0 border-0 p-0 outline-none">
              {loadingQueue ? (
                <div className="rounded-lg border border-border bg-card py-12 flex flex-col items-center justify-center gap-2">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="text-xs text-muted-foreground">Loading approvals...</span>
                </div>
              ) : contactGroups.length === 0 ? (
                <div className="rounded-lg border border-border bg-card py-12 text-center text-muted-foreground">
                  No individual actions awaiting approval.
                </div>
              ) : (
                <div className="space-y-4">
              {contactGroups.map((group) => (
                <div key={group.key} className="rounded-xl border border-border bg-card overflow-hidden">
                  <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-muted/30">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold">
                      {group.name.charAt(0).toUpperCase()}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-foreground truncate">{group.name}</p>
                      {group.detail && <p className="text-xs text-muted-foreground truncate">{group.detail}</p>}
                    </div>
                    <span className="shrink-0 inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700">
                      {group.items.length} pending
                    </span>
                  </div>
                  <div className="divide-y divide-border">
                    {group.items.map((req) => (
                      <div
                        key={req.id}
                        onClick={() => setActionModalId(req.id)}
                        className="group flex flex-col gap-2 px-4 py-3 cursor-pointer hover:bg-muted/30 transition-colors sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground" title={req.action_name}>{formatActionName(req.action_name)}</span>
                            <RiskTier name={req.action_name} />
                            <span className="text-xs text-muted-foreground whitespace-nowrap">{formatDistanceToNow(new Date(req.created_at), { addSuffix: true })}</span>
                          </div>
                          {replyDraftText(req.inputs) ? (
                            <p className="text-xs text-muted-foreground line-clamp-2">“{replyDraftText(req.inputs)}”</p>
                          ) : req.reasoning_trace ? (
                            <p className="text-xs text-muted-foreground line-clamp-2">{req.reasoning_trace}</p>
                          ) : null}
                        </div>
                        <div className="flex gap-2.5 shrink-0 sm:pt-0.5">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 transition-colors"
                            onClick={(e) => { e.stopPropagation(); handleDecision(req.id, 'reject'); }}
                            disabled={processingId === req.id}
                          >
                            <XCircle className="h-3.5 w-3.5 mr-1" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-sm transition-colors"
                            onClick={(e) => { e.stopPropagation(); handleDecision(req.id, 'approve'); }}
                            disabled={processingId === req.id}
                          >
                            {processingId === req.id ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5 mr-1" />}
                            Approve
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
                </div>
              )}
            </TabsContent>

            {/* Campaigns — cross-contact batches reviewed as a whole. */}
            <TabsContent value="campaigns" className="mt-0 border-0 p-0 outline-none">
              {loadingQueue ? (
                <div className="rounded-lg border border-border bg-card py-12 flex flex-col items-center justify-center gap-2">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="text-xs text-muted-foreground">Loading campaigns...</span>
                </div>
              ) : batches.length === 0 ? (
                <div className="rounded-lg border border-border bg-card py-12 text-center text-muted-foreground">
                  No campaigns awaiting approval.
                </div>
              ) : (
                <div className="rounded-xl border border-border bg-card overflow-hidden">
                  <div className="divide-y divide-border">
                    {batches.map((req) => {
                      const b = parseBatchInputs(req);
                      return (
                        <div
                          key={req.id}
                          onClick={() => setBatchModalId(req.id)}
                          className="group flex flex-col gap-2 px-4 py-3 cursor-pointer hover:bg-amber-50/30 transition-colors sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="min-w-0 flex-1 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium text-foreground">Execute winback campaign</span>
                              <span className="text-xs text-muted-foreground whitespace-nowrap">
                                {b.recipients.length} recipient{b.recipients.length === 1 ? "" : "s"} · {formatDistanceToNow(new Date(req.created_at), { addSuffix: true })}
                              </span>
                            </div>
                            {req.reasoning_trace && (
                              <p className="text-xs text-muted-foreground line-clamp-2">{req.reasoning_trace}</p>
                            )}
                          </div>
                          <div className="flex gap-2.5 shrink-0 sm:pt-0.5">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 transition-colors"
                              onClick={(e) => { e.stopPropagation(); handleBatchDecision(req.id, 'reject'); }}
                              disabled={processingBatchId === req.id}
                            >
                              <XCircle className="h-3.5 w-3.5 mr-1" />
                              Reject all
                            </Button>
                            <Button
                              size="sm"
                              className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-sm transition-colors"
                              onClick={(e) => { e.stopPropagation(); handleBatchDecision(req.id, 'approve'); }}
                              disabled={processingBatchId === req.id}
                            >
                              {processingBatchId === req.id ? (
                                <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                              ) : (
                                <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
                              )}
                              Approve all
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
      ) : (
        /* Audit logs view */
        <div className="rounded-lg border border-border overflow-hidden bg-card">
            <Table className="min-w-[680px]">
              <TableHeader>
                <TableRow className="hover:bg-transparent [&>th]:bg-background">
                  <ActionHeaderCell />
                  <TableHead>Status</TableHead>
                  <TableHead>Executor</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead className="w-10" aria-label="Details" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingAudit ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-12 text-center">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                        <span className="text-xs text-muted-foreground">Loading audit logs...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (auditLogs ?? []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-12 text-center text-muted-foreground">
                      No decisions logged yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  auditLogs?.map((log) => {
                    const executor = executorFor(log.executor_type);
                    const ExecutorIcon = executor.icon;
                    return (
                    <TableRow
                      key={log.id}
                      className="hover:bg-muted/30 transition-colors"
                    >
                      <TableCell>
                        <div className="font-medium text-foreground text-sm" title={log.action_name}>{formatActionName(log.action_name)}</div>
                      </TableCell>
                      <TableCell>
                        {statusBadge(log.status)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                          <ExecutorIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                          {executor.label}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm font-medium text-foreground">{format(new Date(log.created_at), 'MMM d, yyyy - h:mm a')}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{formatDistanceToNow(new Date(log.created_at), { addSuffix: true })}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        {/* Technical detail (inputs/outputs, latency, version) lives behind
                            this menu — keeps the table clean for non-technical owners. */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                              aria-label="Open actions menu"
                              title="Actions"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => setAuditModalId(log.id)}>
                              <Terminal className="h-4 w-4 mr-2" />
                              Show console log
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
        </div>
      )}

      <Dialog open={!!selectedBatch} onOpenChange={(o) => !o && setBatchModalId(null)}>
        <DialogContent className="sm:max-w-[720px] max-h-[85vh] overflow-hidden flex flex-col p-0">
          {selectedBatch && (
            <>
              <DialogHeader className="px-6 pt-6 pb-3 border-b border-border">
                <DialogTitle className="text-base font-semibold flex items-center gap-2 font-heading">
                  <Send className="h-4 w-4 text-amber-600" />
                  Execute winback campaign · {parseBatchInputs(selectedBatch).campaignLabel}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  <span className="font-semibold text-foreground">
                    {parseBatchInputs(selectedBatch).recipients.length} recipient
                    {parseBatchInputs(selectedBatch).recipients.length === 1 ? "" : "s"}
                  </span>{" "}
                  matched the{" "}
                  <span className="font-semibold text-foreground">dormant_60d</span> segment · Queued{" "}
                  <span className="font-semibold text-foreground">
                    {formatDistanceToNow(new Date(selectedBatch.created_at), { addSuffix: true })}
                  </span>
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto">
                <WinbackBatchDetails
                  batch={selectedBatch}
                  includeImages={batchIncludeImages}
                  onIncludeImagesChange={setBatchIncludeImages}
                />
              </div>
              <DialogFooter className="px-6 py-4 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 transition-colors"
                  onClick={() => { handleBatchDecision(selectedBatch.id, 'reject'); setBatchModalId(null); }}
                  disabled={processingBatchId === selectedBatch.id}
                >
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />
                  Reject all
                </Button>
                <Button
                  size="sm"
                  className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-sm transition-colors"
                  onClick={() => { handleBatchDecision(selectedBatch.id, 'approve'); setBatchModalId(null); }}
                  disabled={processingBatchId === selectedBatch.id}
                >
                  {processingBatchId === selectedBatch.id ? (
                    <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  Approve all
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedAction} onOpenChange={(o) => !o && setActionModalId(null)}>
        <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-hidden flex flex-col p-0">
          {selectedAction && (
            <>
              <DialogHeader className="px-6 pt-6 pb-3 border-b border-border">
                <DialogTitle className="text-base font-semibold flex items-center gap-2 font-heading">
                  {formatActionName(selectedAction.action_name)}
                  <RiskTier name={selectedAction.action_name} />
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {format(new Date(selectedAction.created_at), "MMM d, yyyy 'at' h:mm a")} · Queued{" "}
                  {formatDistanceToNow(new Date(selectedAction.created_at), { addSuffix: true })}
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-6 py-4">
                <p className="text-[11px] uppercase tracking-wider text-muted-foreground/80 font-semibold mb-2">Inputs</p>
                <pre className="p-3 bg-muted/65 border border-border rounded-lg text-[11px] font-mono overflow-x-auto whitespace-pre leading-normal text-muted-foreground shadow-inner">
                  {JSON.stringify(selectedAction.inputs, null, 2)}
                </pre>
              </div>
              <DialogFooter className="px-6 py-4 border-t border-border">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-xs text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 transition-colors"
                  onClick={() => { handleDecision(selectedAction.id, 'reject'); setActionModalId(null); }}
                >
                  <XCircle className="h-3.5 w-3.5 mr-1.5" />
                  Reject
                </Button>
                <Button
                  size="sm"
                  className="h-8 text-xs bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-sm transition-colors"
                  onClick={() => { handleDecision(selectedAction.id, 'approve'); setActionModalId(null); }}
                >
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />
                  Approve
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!selectedAudit} onOpenChange={(o) => !o && setAuditModalId(null)}>
        <DialogContent className="sm:max-w-[640px] max-h-[85vh] overflow-hidden flex flex-col p-0">
          {selectedAudit && (
            <>
              <DialogHeader className="px-6 pt-6 pb-3 border-b border-border">
                <DialogTitle className="text-base font-semibold flex items-center gap-2 font-heading">
                  {formatActionName(selectedAudit.action_name)}
                  {statusBadge(selectedAudit.status)}
                </DialogTitle>
                <DialogDescription className="text-xs">
                  {executorFor(selectedAudit.executor_type).label} · {format(new Date(selectedAudit.created_at), "MMM d, yyyy 'at' h:mm a")}
                  {selectedAudit.latency_ms ? ` · ${selectedAudit.latency_ms}ms` : ""}
                </DialogDescription>
              </DialogHeader>
              <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground/80 font-semibold mb-2">Inputs</p>
                  <pre className="p-3 bg-muted/65 border border-border rounded-lg text-[11px] font-mono overflow-x-auto whitespace-pre leading-normal text-muted-foreground shadow-inner">
                    {JSON.stringify(selectedAudit.inputs, null, 2)}
                  </pre>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground/80 font-semibold mb-2">Outputs</p>
                  {selectedAudit.outputs ? (
                    <pre className="p-3 bg-muted/65 border border-emerald-100 rounded-lg text-[11px] font-mono overflow-x-auto whitespace-pre leading-normal text-muted-foreground shadow-inner">
                      {JSON.stringify(selectedAudit.outputs, null, 2)}
                    </pre>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">No output recorded.</p>
                  )}
                </div>
                {selectedAudit.error && (
                  <div>
                    <p className="text-[11px] uppercase tracking-wider text-red-500/80 font-semibold mb-2">Error</p>
                    <pre className="p-3 bg-red-500/5 border border-red-200 rounded-lg text-[11px] font-mono overflow-x-auto whitespace-pre-wrap leading-normal text-red-600 shadow-inner">
                      {selectedAudit.error}
                    </pre>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Winback batch recipient review
// Campaign batch rows open this in a modal so the owner can review every
// recipient before approving the send.

const parseBatchInputs = (batch: ApprovalRequest) => {
  const inputs = (batch.inputs ?? {}) as {
    campaign_id?: string;
    recipients?: Array<{
      contact_id: string;
      channel: string;
      decision_trace?: any;
    }>;
  };
  const recipients = inputs.recipients ?? [];
  const campaignLabel = inputs.campaign_id?.startsWith("dormant_winback:")
    ? inputs.campaign_id.split(":")[1]
    : inputs.campaign_id ?? "Winback batch";
  return { recipients, campaignLabel };
};

const WinbackBatchDetails = ({
  batch,
  includeImages,
  onIncludeImagesChange,
}: {
  batch: ApprovalRequest;
  includeImages: boolean;
  onIncludeImagesChange: (next: boolean) => void;
}) => {
  const { recipients } = parseBatchInputs(batch);
  const contactIds = recipients.map((r) => r.contact_id);
  // Whether any recipient is on an image-capable channel with a hero resolved
  // at queue time. Drives whether the image
  // toggle is worth showing.
  const anyImageCapableRecipient = recipients.some(
    (r) =>
      ["email", "telegram", "instagram", "facebook_messenger", "line"].includes(
        r.channel,
      ),
  );

  const { data: contactMap } = useQuery({
    queryKey: ["winback-recipient-contacts", batch.id],
    queryFn: async () => {
      if (contactIds.length === 0) return {} as Record<string, { name: string }>;
      const { data, error } = await supabase
        .from("contacts")
        .select("contact_id, display_name, primary_email, primary_phone")
        .in("contact_id", contactIds);
      if (error) throw error;
      const map: Record<string, { name: string }> = {};
      (data ?? []).forEach((c: any) => {
        map[c.contact_id] = {
          name: c.display_name || c.primary_email || c.primary_phone || `Contact ${String(c.contact_id).slice(0, 8)}`,
        };
      });
      return map;
    },
    enabled: contactIds.length > 0,
  });

  return (
    <div className="px-5 pt-3 pb-4 space-y-4">
      {batch.reasoning_trace && (
        <div className="rounded-lg bg-primary/5 border border-primary/20 px-4 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-primary mb-1">Batch summary</p>
          <p className="text-sm text-foreground leading-relaxed">{batch.reasoning_trace}</p>
        </div>
      )}
      {anyImageCapableRecipient && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border bg-muted/20 px-4 py-3">
          <div className="flex items-center gap-2.5 min-w-0">
            {includeImages
              ? <ImageIcon className="h-4 w-4 text-muted-foreground shrink-0" />
              : <ImageOff className="h-4 w-4 text-muted-foreground shrink-0" />}
            <div className="min-w-0">
              <p className="text-sm font-medium text-foreground">Include images</p>
              <p className="text-xs text-muted-foreground leading-snug">
                {includeImages
                  ? "Recipients on image-capable channels get a listing photo."
                  : "Text-only for this batch."}
              </p>
            </div>
          </div>
          <Switch checked={includeImages} onCheckedChange={onIncludeImagesChange} />
        </div>
      )}
      {recipients.length === 0 ? (
        <p className="py-6 text-sm text-muted-foreground text-center">No recipients in this batch.</p>
      ) : (
        <div className="space-y-3">
          {recipients.map((r) => {
            const signals = (r.decision_trace ?? {}).signals ?? {};
            const channelDecision = (r.decision_trace ?? {}).channel_decision ?? {};
            const unsendable = channelDecision.sendable === false;
            const skipHint = SKIP_REASON_LABELS[channelDecision.skip_reason as string]
              ?? "Can't be sent on this channel right now.";
            const name =
              contactMap?.[r.contact_id]?.name ?? `Contact ${r.contact_id.slice(0, 8)}`;
            // Preview thumbnail: image-capable recipients
            // with a hero resolved at queue time, shown only while the image
            // toggle is on.
            const heroUrl = (r.decision_trace ?? {}).hero_image_url as string | undefined;
            const showHero =
              includeImages &&
              ["email", "telegram", "instagram", "facebook_messenger", "line"].includes(
                r.channel,
              ) &&
              !!heroUrl;
            return (
              <div
                key={r.contact_id}
                className="rounded-xl border border-border bg-card overflow-hidden"
              >
                <div className="px-4 pt-3.5 pb-3 flex items-start justify-between gap-3">
                  {showHero && (
                    <img
                      src={heroUrl}
                      alt=""
                      loading="lazy"
                      className="h-11 w-11 rounded-lg object-cover border border-border shrink-0"
                    />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-foreground truncate">{name}</p>
                    {signals.synopsis && (
                      <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{signals.synopsis}</p>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <Badge
                      variant="outline"
                      className="capitalize text-[10px] py-0.5 px-2 rounded-full border-border bg-muted text-muted-foreground font-medium hover:bg-muted"
                    >
                      {r.channel}
                    </Badge>
                    {unsendable && (
                      <Badge
                        variant="outline"
                        className="text-[10px] py-0.5 px-2 rounded-full border-amber-300 bg-amber-50 text-amber-800 font-medium hover:bg-amber-50"
                        title={skipHint}
                      >
                        Won't send
                      </Badge>
                    )}
                  </div>
                </div>
                {unsendable && (
                  <p className="px-4 pb-2 -mt-1 text-[11px] text-amber-700 leading-snug">{skipHint}</p>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-border border-t border-border bg-muted/20">
                  <MiniSignal label="Last active" value={signals.days_since_last_activity != null ? `${signals.days_since_last_activity}d ago` : "—"} />
                  <MiniSignal label="Last booking" value={signals.last_booking_value != null ? `$${Number(signals.last_booking_value).toFixed(2)}` : "—"} />
                  <MiniSignal label="Bookings" value={signals.total_bookings ?? "—"} />
                  <MiniSignal label="LTV" value={signals.ltv != null ? `$${Number(signals.ltv).toFixed(2)}` : "—"} />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Friendly explanations for plan-time sendability skips (set by the winback
// batch planner in dormantWinbackFromSegment.ts → channel_decision.skip_reason).
const SKIP_REASON_LABELS: Record<string, string> = {
  outside_window_no_approved_template:
    "Outside WhatsApp's 24h window and no approved template yet. Add one under Storefront AI → Offers to reach this customer.",
  outside_window_no_template_channel:
    "Outside the 24h messaging window and this channel has no template path. They won't receive this win-back.",
  no_outbound_push_channel: "This contact only has a web identifier, which can't receive outbound messages.",
};

const MiniSignal = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="px-3 py-2.5 space-y-0.5">
    <p className="text-[9px] uppercase tracking-wider text-muted-foreground/80 font-medium">{label}</p>
    <p className="text-xs font-semibold text-foreground">{value}</p>
  </div>
);

export default ActionCenter;
